import { usePreferences } from 'context/preferences/PreferencesContext'

/**
 * Convert snake_case to camelCase
 * Examples:
 *   enable_campaigns -> enableCampaigns
 *   show_menu_column -> showMenuColumn
 */
const snakeToCamel = (str: string): string =>
  str.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase())

/**
 * KNOWN FLAGS (non-exhaustive; flags are read dynamically from preferences).
 * Documenting new flags here keeps them greppable since there is no static enum.
 *
 * Homepage flags (FIR-7902 — split of the former `enable_homepage`):
 *   - enable_homepage_basics -> enableHomepageBasics — basic homepage only
 *     (greeting, Quick Access, Performance Dashboard).
 *   - enable_action_center   -> enableActionCenter   — full homepage: everything
 *     basics has PLUS the recommendation Action Center + AI Settings. It is a
 *     self-sufficient superset (does not require enableHomepageBasics).
 *   See `isHomepageEnabled()` below for the "homepage is visible" predicate.
 *
 * Contact detail page (off by default — dark launch):
 *   - enable_contact_detail_page -> enableContactDetailPage — gates the contacts
 *     table's name link to `/contacts/detail/:id` and the page itself (a
 *     flag-off direct URL redirects to /contacts). Flip on per company to
 *     reveal the page.
 *
 * Deal detail page v2 (off by default — dark launch):
 *   - enable_deal_detail_page_v2 -> enableDealDetailPageV2 — swaps the deal
 *     detail page over to the @firstbite/ui rebuild (DealDetailPageFbui); flag
 *     off → the legacy DealDetailPage. "v2" because a deal detail page already
 *     exists — this gates the redesign. Managed in PostHog (create the flag with
 *     this exact key; targets user distinct_id / company_id).
 *
 * Contact modal redesign (off by default — dark launch):
 *   - enable_ds2026_contact_modal -> enableDs2026ContactModal — gates the
 *     @firstbite/ui (DS2026) rebuild of the Add + Edit Contact modal (drives the
 *     `ds2026` prop in NewContactModal + EditContactModal). Flag off → the legacy
 *     modal. Backend must register the flag before it can be enabled per company.
 */

/**
 * LEGACY ALIASES - Maps snake_case backend keys to legacy frontend variable names.
 *
 * These exist because the original code used custom variable names that don't match
 * a simple snake_case -> camelCase conversion. New flags should NOT be added here.
 *
 * To phase out: Search for the legacy name, update consumers to use the auto-generated
 * camelCase name, then remove the entry from this map.
 */
const LEGACY_ALIASES: Record<string, string> = {
  // Prefix changes (show_ -> shouldShow)
  show_menu_size: 'shouldShowMenuSize',
  show_menu_column: 'shouldShowMenu',
  reputation_data: 'shouldShowReputationData',
  show_door_menu_column: 'shouldShowDoorMenu',

  // Typo preserved for backward compat
  enable_instagram_data: 'enableInstagremData',

  // Singular vs plural
  enable_instagram_percentiles: 'enableInstagramPercentile',

  // Suffix removal (_table removed)
  enable_hospitals_table: 'enableHospitals',
  enable_nursing_homes_table: 'enableNursingHomes',
  enable_lodging_table: 'enableLodging',
  enable_convenience_table: 'enableConvenience',

  // Acronym casing (SSO, HQ, AI, CnU)
  enable_cnu_table: 'enableCnUTable',
  enable_hq_state_column: 'enableHQStateColumn',
  enable_sso: 'enableSSO',
  enable_ai_sequence_generation: 'enableAISequenceGeneration',

  // Underscore in number preserved
  enable_deals_ltv_1_5: 'enableDealsLtv1_5',

  // Name changes (prefix added or word changed)
  enable_import_wizard: 'enableImporterWizard',
  campaign_list_use_denormalized_stats: 'enableCampaignDenormalizedStats',
}

// Return type includes isLoading plus any string key for dynamic flags
type FeatureFlagReturn = Record<string, boolean> & { isLoading: boolean }

export const useFeatureFlagService = (): FeatureFlagReturn => {
  const { preferences, isLoading } = usePreferences()

  const flags: Record<string, boolean> = {}

  if (preferences?.feature_flags) {
    for (const [key, value] of Object.entries(preferences.feature_flags)) {
      const enabled = value.enabled ?? false

      // Auto-generate camelCase key
      const camelKey = snakeToCamel(key)
      flags[camelKey] = enabled

      // Also add legacy alias if one exists
      const legacyAlias = LEGACY_ALIASES[key]
      if (legacyAlias) {
        flags[legacyAlias] = enabled
      }
    }
  }

  return {
    ...flags,
    isLoading,
  } as FeatureFlagReturn
}

// Type for consumers - any flag name returns boolean
export type FeatureFlags = Record<string, boolean>

/**
 * "Homepage is visible" predicate (FIR-7902).
 *
 * The homepage — its `/home` route, the default-landing redirect, and the Home
 * nav item — is shown when EITHER homepage flag is enabled:
 *   - `enableHomepageBasics` — basic homepage only.
 *   - `enableActionCenter`   — full homepage (basics + Action Center + AI
 *     Settings). Self-sufficient superset.
 *
 * Action-Center-specific surfaces (the Action Center card, AI Settings, and the
 * Email Signatures tab) gate on `enableActionCenter` alone — NOT this predicate.
 */
export const isHomepageEnabled = (flags: FeatureFlags): boolean =>
  Boolean(flags.enableActionCenter || flags.enableHomepageBasics)
